import { memo, useState, useEffect, useMemo } from "react";
import { Chip, Grid2, IconButton, TextField, Tooltip } from "@mui/material";
import { icon } from "@/app/style/icons";
import {
    selectDocumentFilters,
    setJurisdictionFilter,
    setSearchTerm,
    setSortField,
    setSortOrder,
    DocumentSortField,
} from "@store/slices/documentSelection";
import { DocumentSortFieldEnum, DocumentSortOrderEnum } from "@/app/graphql/codegen";
import { useDispatch, useSelector } from "react-redux";
import { Label } from "@/types/enums";
import debounce from "lodash/debounce";
import { color } from "@/app/style/color";
import { alpha } from "@mui/material/styles";

type FilterAction = "search" | "filter" | "sort";

const LexiconDocumentFilter = memo(() => {
    const dispatch = useDispatch();

    // Aktuelle Filter/Sortierkriterien aus dem Store
    const filters = useSelector(selectDocumentFilters);

    // Aktivierte Filteraktionen, die angezeigt werden sollen
    const [activeActions, setActiveActions] = useState<FilterAction[]>([]);
    const toggleAction = (action: FilterAction) => {
        setActiveActions((current) => {
            const isCurrentlyActive = current.includes(action);
            
            // If we're turning OFF the search action, clear the search term
            if (action === "search" && isCurrentlyActive) {
                setSearchInput("");
                dispatch(setSearchTerm(""));
            }
            
            // If we're turning OFF the filter action, reset jurisdiction to 'all'
            if (action === "filter" && isCurrentlyActive) {
                dispatch(setJurisdictionFilter("all"));
            }
            
            // If we're turning OFF the sort action, you might reset to default if desired
            // For now we keep current sort settings and just hide the chips
            
            return isCurrentlyActive ? current.filter((a) => a !== action) : [...current, action];
        });
    };

    // Suchbegriff mit Debounce - create stable debounce function
    const [searchInput, setSearchInput] = useState(filters.searchTerm);
    
    // Create debounce function only once, never recreate it
    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            dispatch(setSearchTerm(value));
        }, 500),
        [dispatch]
    );

    // Initialize search action if there's an existing search term
    useEffect(() => {
        if (filters.searchTerm && !activeActions.includes("search")) {
            setActiveActions(current => [...current, "search"]);
        }
    }, [filters.searchTerm, activeActions]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    const handleJurisdictionChange = (value: string) => {
        dispatch(setJurisdictionFilter(value));
    };

    const handleSortChange = (field: DocumentSortField) => {
        if (field === filters.sortField) {
            // Toggle sort order if same field
            dispatch(setSortOrder(filters.sortOrder === DocumentSortOrderEnum.Asc ? DocumentSortOrderEnum.Desc : DocumentSortOrderEnum.Asc));
        } else {
            // Set new field and default to descending order
            dispatch(setSortField(field));
            dispatch(setSortOrder(DocumentSortOrderEnum.Desc));
        }
    };

    return (
        <Grid2 container size={12} spacing={1.5}>
            <Grid2 container size={12} spacing={1} justifyContent={"space-between"}>
                <Grid2 container>
                    <Grid2>
                        <IconButton
                            onClick={() => toggleAction("search")}
                            color={activeActions.includes("search") ? "secondary" : "default"}
                            sx={{ background: activeActions.includes("search") ? alpha(color.brand.one, 0.25) : undefined }}
                        >
                            <icon.search sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>

                    <Grid2>
                        <IconButton
                            onClick={() => toggleAction("filter")}
                            color={activeActions.includes("filter") ? "secondary" : "default"}
                            sx={{ background: activeActions.includes("filter") ? alpha(color.brand.one, 0.25) : undefined }}
                        >
                            <icon.filter sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>

                    <Grid2>
                        <IconButton
                            onClick={() => toggleAction("sort")}
                            color={activeActions.includes("sort") ? "secondary" : "default"}
                            sx={{ background: activeActions.includes("sort") ? alpha(color.brand.one, 0.25) : undefined }}
                        >
                            <icon.order sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>
                </Grid2>

                <Grid2 container>
                    <Grid2>
                        <Tooltip title="Hier kannst du Dokumente nach verschiedenen Kriterien filtern und sortieren. Verwende die Suchfunktion, um nach Stichworten zu suchen.">
                            <IconButton>
                                <icon.info sx={{ color: "semantic.icon.informative" }} />
                            </IconButton>
                        </Tooltip>
                    </Grid2>
                </Grid2>
            </Grid2>

            {activeActions.length > 0 && (
                <Grid2 container size={12} spacing={2}>
                    {activeActions.includes("search") && (
                        <Grid2 size={12}>
                            <TextField fullWidth placeholder={Label.SEARCH} value={searchInput} onChange={(e) => handleSearchChange(e.target.value)} />
                        </Grid2>
                    )}

                    {activeActions.includes("filter") && (
                        <Grid2 container size={12} spacing={1.5}>
                            {[
                                { label: "All", value: "all" },
                                { label: "EU", value: "EU" },
                                { label: "DE", value: "DE" },
                                { label: "DE Landesrecht", value: "DE_LANDESRECHT" },
                                { label: "Sonstige", value: "OTHER" },
                            ].map((j) => (
                                <Grid2 key={j.value}>
                                    <Chip
                                        clickable
                                        label={j.label}
                                        onClick={() => handleJurisdictionChange(j.value)}
                                        variant={filters.jurisdiction === j.value ? "filled" : "outlined"}
                                    />
                                </Grid2>
                            ))}
                        </Grid2>
                    )}

                    {activeActions.includes("sort") && (
                        <Grid2 container size={12} spacing={1.5}>
                            <Grid2>
                                <Chip
                                    clickable
                                    label={`Titel ${filters.sortField === DocumentSortFieldEnum.Title ? (filters.sortOrder === DocumentSortOrderEnum.Asc ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange(DocumentSortFieldEnum.Title)}
                                    variant={filters.sortField === DocumentSortFieldEnum.Title ? "filled" : "outlined"}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    clickable
                                    label={`Datum ${filters.sortField === DocumentSortFieldEnum.Date ? (filters.sortOrder === DocumentSortOrderEnum.Asc ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange(DocumentSortFieldEnum.Date)}
                                    variant={filters.sortField === DocumentSortFieldEnum.Date ? "filled" : "outlined"}
                                />
                            </Grid2>

                            <Grid2>
                                <Chip
                                    clickable
                                    label={`Größe ${filters.sortField === DocumentSortFieldEnum.Size ? (filters.sortOrder === DocumentSortOrderEnum.Asc ? "↑" : "↓") : ""}`}
                                    onClick={() => handleSortChange(DocumentSortFieldEnum.Size)}
                                    variant={filters.sortField === DocumentSortFieldEnum.Size ? "filled" : "outlined"}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Grid2>
            )}
        </Grid2>
    );
});

export default LexiconDocumentFilter;
